import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
// Direct path, not the _entities barrel (which pulls the store in).
import { getTimeZone } from '_entities/Auth/Auth.selectors';
import { fetchConstructor } from '_entities/old/api/api';
import { Loader } from 'ui-kit';
import { NoteItem, NotesStyled as Styled } from 'modules/RetailerNotes';

/**
 * This visit's notes (P20M-1242 R14): the visit's own "Notes" field plus every answered note question
 * of its form, with the photos each note question owns — exactly what this visit contributes to the
 * retailer's Notes tab, and nothing else. The retailer's own manual notes and the sibling visits stay
 * on the retailer page; here the subject is the visit.
 *
 * These rows are a projection over the visit's fields, so they are edited where they live — on the
 * visit form (`can_edit`/`can_delete` are always false). Each note's pencil opens that form at the
 * question the note is stored in, for users who may edit the visit.
 *
 * Visibility is the nav item's job (the `retailer_notes` account module); seeing the visit is enough to
 * see its notes, because every row here is already on screen inside the form answers.
 */

// Notes derived from a visit carry no id, and one visit can hold several (the Notes field plus one per
// note question), so they key off the reason and position rather than an id.
const noteKey = (note, index) => `visit-note-${index}-${note.reason || ''}`;

/** The Edit Visit link for one note: the form opened at the question it's stored in (`question` on
 *  the row — "notes" for the visit's own Notes field). Null when the user can't edit the visit. */
const noteEditHref = (editUrl, note) =>
  editUrl && note.question ? `${editUrl}?question=${encodeURIComponent(note.question)}` : null;

const VisitNotes = ({ visitId, resource, editUrl }) => {
  const timeZone = useSelector(getTimeZone);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!visitId || !resource) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchConstructor('get', `${resource}/${visitId}/notes`)
      .then((response) => {
        if (cancelled) return;
        setNotes(response?.status === 200 ? response.data.results || [] : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, resource]);

  if (loading) return <Loader />;

  if (notes.length === 0) {
    return <Styled.Empty>No notes for this visit.</Styled.Empty>;
  }

  return (
    <div>
      {notes.map((note, index) => (
        // Already on this visit: no badge naming its type, no link back to it.
        <NoteItem
          key={noteKey(note, index)}
          note={note}
          timeZone={timeZone}
          hideVisitContext
          editHref={noteEditHref(editUrl, note)}
        />
      ))}
    </div>
  );
};

VisitNotes.propTypes = {
  visitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The visit-type API resource this page belongs to: enforcements / educations / assessments.
  resource: PropTypes.string,
  // This visit's Edit Visit page, when the user may edit it (the Edit button's rule); null otherwise.
  editUrl: PropTypes.string,
};

VisitNotes.defaultProps = {
  visitId: null,
  resource: '',
  editUrl: null,
};

export default VisitNotes;
